import { useState } from 'react';
import { Braces, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { publicUrl } from '@/lib/env';
import { cn } from '@/lib/utils';
import type { MensajeChat } from '@/types/conversations';
import {
  FOTO_SIN_PREVISUALIZAR,
  describeErrorEnvio,
  direccionLabel,
  entregaText,
  errorDeVentana,
  esUrlDeImagen,
  formatMessageTime,
  mensajeNoEntregado,
  prettyJsonMessage,
  tipoMensajeLabel,
} from './inbox-format';

/**
 * Una burbuja del historial.
 *
 * El lado y el color dicen quién habló: el negocio a la derecha (fondo azul de marca; dentro caben el
 * bot y un asesor) y el cliente a la izquierda (fondo blanco). Se repite el nombre en cada burbuja
 * porque en una conversación de WhatsApp aparecen mensajes de las dos partes entrelazados y a las 3
 * de la mañana nadie quiere adivinar de quién es una línea.
 *
 * Tres formas de contenido, en este orden:
 *  1. **una foto** (`tipo = IMAGEN` y el contenido es una URL): se pinta la imagen —y al hacer clic se
 *     abre en grande— resolviendo la ruta relativa de las fotos propias (`/uploads/…` → origen del
 *     API, con `publicUrl`). Si el navegador no la puede cargar (una foto ENTRANTE anterior al
 *     24-sep-2026, que guardaba la URL temporal de Meta —esa exige el token del canal y caduca—, o una
 *     URL rota), se explica y se deja la URL;
 *  2. **el JSON de un pedido** (el cliente lo pega en el chat): se colapsa en un `<details>`, porque
 *     entero ocuparía varias pantallas y taparía la conversación (`prettyJsonMessage` decide si el
 *     contenido es uno de esos bloques);
 *  3. **texto normal** (y un mensaje sin contenido se explica en vez de quedar en blanco).
 *
 * Y una capa de ENTREGA: cuando el canal rechazó el saliente (`entregado === false`) la burbuja lo
 * dice, con el motivo de Meta y un botón para reintentarlo (`onResend` → `useResendMessage`).
 */

export interface MessageBubbleProps {
  mensaje: MensajeChat;
  /** Reenvía este mensaje (solo se usa cuando el canal lo rechazó). Sin él, no se pinta el botón. */
  onResend?: (mensaje: MensajeChat) => void;
  /** Hay un reenvío en curso para ESTE mensaje (bloquea el botón y lo deja cargando). */
  resending?: boolean;
}

export function MessageBubble({ mensaje, onResend, resending = false }: MessageBubbleProps) {
  const delBot = mensaje.direccion === 'OUT';
  const json = prettyJsonMessage(mensaje.contenido);
  const tipo = tipoMensajeLabel[mensaje.tipo];
  const vacio = !json && mensaje.contenido.trim().length === 0;

  /** Una foto que el navegador no pudo cargar pasa a explicarse (con la URL a la vista). */
  const [fotoRota, setFotoRota] = useState(false);
  const foto = mensaje.tipo === 'IMAGEN' && !json && esUrlDeImagen(mensaje.contenido);
  const mostrarFoto = foto && !fotoRota;

  // Aviso de entrega: solo cuando el canal RECHAZÓ el mensaje (`false` explícito). Los `null` (los
  // entrantes y todo lo anterior a la columna) no se afirman, y los entregados no se comentan.
  const noEntregado = mensajeNoEntregado(mensaje);
  const motivoRechazo = describeErrorEnvio(mensaje.errorEnvio);
  const ventanaCerrada = errorDeVentana(mensaje.errorEnvio);

  return (
    <li className={cn('flex', delBot ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-panel border px-3 py-2 text-caption',
          delBot
            ? 'border-brand-100 bg-brand-50 text-ink'
            : 'border-line bg-surface text-ink-soft',
        )}
      >
        <div className="mb-1 flex items-center gap-2 text-micro text-ink-faint">
          <span className="font-semibold">{direccionLabel[mensaje.direccion]}</span>

          {/* El tipo solo se dice cuando NO es texto: "Texto" en cada burbuja sería ruido. */}
          {mensaje.tipo !== 'TEXTO' && !mostrarFoto ? <Badge tone="neutral">{tipo}</Badge> : null}

          <span>{formatMessageTime(mensaje.createdAt)}</span>
        </div>

        {mostrarFoto ? (
          <a
            href={publicUrl(mensaje.contenido)}
            target="_blank"
            rel="noreferrer"
            className="block"
            title="Abrir la foto"
          >
            <img
              src={publicUrl(mensaje.contenido)}
              alt={`Foto ${delBot ? 'enviada al cliente' : 'del cliente'}`}
              className="max-h-64 w-full max-w-xs rounded-control border border-line object-cover"
              loading="lazy"
              onError={() => setFotoRota(true)}
            />
          </a>
        ) : json ? (
          <details className="group">
            <summary
              className={cn(
                'flex cursor-pointer items-center gap-1.5 font-semibold text-brand-700',
                'marker:content-none',
              )}
            >
              <Braces size={13} aria-hidden="true" />
              Ver los datos del pedido (JSON)
            </summary>

            <pre className="scroll-thin mt-2 max-h-64 overflow-auto rounded-control border border-line bg-surface p-2 text-micro whitespace-pre-wrap">
              {json}
            </pre>
          </details>
        ) : (
          <p className="break-words whitespace-pre-wrap">
            {foto && fotoRota ? (
              <>
                {FOTO_SIN_PREVISUALIZAR}{' '}
                <span className="break-all font-mono text-micro text-ink-faint">
                  {mensaje.contenido}
                </span>
              </>
            ) : vacio ? (
              `(${tipo.toLowerCase()} sin texto)`
            ) : (
              mensaje.contenido
            )}
          </p>
        )}

        {/* Aviso de entrega: el canal rechazó este saliente (el motor guarda la fila igual, así que sin
            esto el panel lo pintaba como enviado y el cliente nunca lo recibió). */}
        {noEntregado ? (
          <div className="mt-2 border-t border-line/80 pt-2">
            <p className="flex items-center gap-1.5 font-semibold text-danger-strong">
              <TriangleAlert size={12} aria-hidden="true" />
              {entregaText.noEntregado}
            </p>

            {motivoRechazo ? (
              <p className="mt-0.5 break-words text-micro text-ink-muted">{motivoRechazo}</p>
            ) : null}

            {ventanaCerrada ? (
              <p className="mt-0.5 text-micro text-ink-muted">{entregaText.usarPlantilla}</p>
            ) : null}

            {onResend ? (
              <Button
                className="mt-2"
                variant="secondary"
                size="sm"
                loading={resending}
                onClick={() => onResend(mensaje)}
              >
                {entregaText.reintentar}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
